// ----------------------------------------------------------------------- imports externos
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  asignadosA,
  habitualesDelTractor,
  tarjetaAjenaDe,
} from "../../services/asignacion";
import { claveDni } from "../../shared/asignacion";
import { useAvisarVacio } from "./BloqueFicha";
import { useRealtime } from "../../services/store";
import { diaAr } from "../../shared/fechaAr";

/* «¿Quién tiene asignado el 581?», en la ficha del camión.
 *
 * La otra mitad del pedido de Nico: «cuando se pregunta por YYY te diga que
 * chofer XXX lo tiene asignado».
 *
 * ── ACÁ NO SE EDITA, Y ESTÁ DECIDIDO ─────────────────────────────────────
 *
 * La fila vive en `chofer_tractor` con el DNI de clave: la asignación es de
 * una persona, y se carga en SU ficha. Repetir el formulario acá serían dos
 * lugares para cargar lo mismo — el error que esta pantalla ya tiene escrito
 * en su encabezado. Lo que sí hay es el enlace a cada chofer, que es donde se
 * cambia.
 *
 * ── SON VARIOS, NO UNO ───────────────────────────────────────────────────
 *
 * Medido sobre las salidas de portería de 180 días: **178 de los 307 tractores
 * que salieron llevaron más de un chofer** (86 llevaron dos, 46 tres, y hay
 * uno con nueve). Doble turno, relevos y francos son lo normal. Un solo
 * renglón acá haría desaparecer al segundo de la pantalla.
 *
 * ── EL NOMBRE SALE DEL STORE, NO DE UNA CONSULTA ─────────────────────────
 *
 * `personas` ya está cargado en el armazón para todas las pantallas. Sin
 * nombre se muestra el DNI y no un renglón en blanco: con el número se busca
 * a la persona, que es más de lo que había.
 */
const ChoferesDelTractor = ({ interno, puedeVerGente = true }) => {
  // undefined = todavía no llegó · null = no se pudo consultar
  const [filas, setFilas] = useState(undefined);
  /* Las dos de abajo arrancan en `undefined` —«todavía no contestó»— y no en
   * vacío: el bloque de la ficha se esconde cuando NO HAY NADA, y para
   * afirmarlo tienen que haber llegado las tres. Ver el aviso más abajo. */
  const [suelen, setSuelen] = useState(undefined);
  const [ajena, setAjena] = useState(undefined);
  const personas = useRealtime("personas");

  useEffect(() => {
    let vigente = true;
    if (!interno) return undefined;
    /* EL `try` ENVUELVE A LA LLAMADA Y NO SÓLO A LA PROMESA: un `.catch()` no
     * atrapa lo que revienta antes de devolverla. Lo encontró un test en el
     * RTO, está escrito en `useBloque` de la ficha, y vale igual acá. */
    try {
      Promise.resolve(asignadosA(interno))
        .then((r) => vigente && setFilas(r))
        .catch(() => vigente && setFilas(null));
    } catch {
      setFilas(null);
    }
    /* La tendencia va aparte y en silencio: es un agregado, y un agregado que
     * falla no puede ensuciar una respuesta que salió bien. */
    try {
      Promise.resolve(habitualesDelTractor(interno))
        .then((r) => vigente && setSuelen(r || []))
        .catch(() => vigente && setSuelen([]));
    } catch {
      setSuelen([]);
    }
    /* La tarjeta de otro, igual: si no se pudo leer no se afirma nada. */
    try {
      Promise.resolve(tarjetaAjenaDe(interno))
        .then((r) => vigente && setAjena(r || null))
        .catch(() => vigente && setAjena(null));
    } catch {
      setAjena(null);
    }
    return () => {
      vigente = false;
    };
  }, [interno]);

  const habituales = suelen || [];

  /* Nadie asignado, portería no propone a nadie y ninguna tarjeta ajena: en la
   * ficha del camión el bloque no se dibuja (Igna, 02/10: «lo mismo para los
   * vehículos» — lo vacío no aparece). Se esperan LAS TRES respuestas antes de
   * decirlo: con la asignación vacía y la tendencia todavía viajando, el
   * bloque se escondería y a los 200 ms volvería con la propuesta.
   *
   * «No se pudo consultar» NO es un vacío y no lo esconde: callarlo sería
   * dibujar una falla igual que un «nadie lo tiene». Afuera de un bloque que
   * lo pida, el aviso no hace nada y esto se dibuja como siempre. */
  useAvisarVacio(
    filas === undefined || suelen === undefined || ajena === undefined
      ? undefined
      : Array.isArray(filas) &&
          filas.length === 0 &&
          habituales.length === 0 &&
          !ajena,
  );

  if (filas === undefined) return null;

  const nombreDe = (dni) => {
    const k = claveDni(dni);
    const p = (personas || []).find((x) => claveDni(x?.dni) === k);
    if (!p) return null;
    const n = `${p.apellido || ""}${p.nombres ? ", " + p.nombres : ""}`.trim();
    return n || null;
  };

  const fila = (dni, extra) => (
    <li key={dni}>
      {puedeVerGente ? (
        <Link to={`/persona/${claveDni(dni)}`}>
          {nombreDe(dni) || `DNI ${dni}`}
        </Link>
      ) : (
        <span>{nombreDe(dni) || `DNI ${dni}`}</span>
      )}
      {extra}
    </li>
  );

  return (
    <div className="ficha-asignado">
      <span className="et">Chofer asignado</span>

      {filas === null && (
        <span className="ficha-asignado-nota">
          No se pudo consultar.
        </span>
      )}

      {filas && filas.length > 0 && (
        <ul className="pf-lista">
          {filas.map((f) =>
            fila(
              f.dni,
              <>
                {f.desde && (
                  <em className="tk-item-meta">
                    {` · desde ${diaAr(f.desde)}`}
                  </em>
                )}
                {f.nota && <em className="tk-item-meta"> · {f.nota}</em>}
              </>,
            ),
          )}
        </ul>
      )}

      {filas && filas.length === 0 && (
        <span className="ficha-asignado-txt tenue">
          Nadie lo tiene asignado.
        </span>
      )}

      {/* LA TARJETA DE OTRO VA PEGADA AL ASIGNADO, que es a quien contradice.
          Portería manda —el asignado no cambia—; lo que se avisa es que el
          tacógrafo tiene puesta la tarjeta de otra persona. Es la misma frase
          que dice el bot por WhatsApp y el globo del mapa. */}
      {ajena && (
        <span className="ficha-asignado-alerta" role="alert">
          Tarjeta de otro chofer: por portería salió{" "}
          <b>{ajena.nombre_porteria || `DNI ${ajena.dni_porteria}`}</b> y el
          tacógrafo tiene la tarjeta de{" "}
          <b>{ajena.nombre_tarjeta || `DNI ${ajena.dni_tarjeta}`}</b>.
        </span>
      )}

      {/* LO QUE DICE PORTERÍA VA APARTE Y CON SU NÚMERO. No es una asignación
          —nadie la escribió— y sin el conteo al lado una tendencia del 90% y
          una del 100% se leen igual. Se muestra sobre todo cuando no hay
          asignación: ahí es trabajo ya hecho que si no nadie aprovecha. */}
      {habituales.length > 0 && (
        <>
          <span className="ficha-asignado-nota">
            Portería dice que suelen salir con él:
          </span>
          <ul className="pf-lista">
            {habituales.map((h) =>
              fila(
                h.dni,
                <em className="tk-item-meta">
                  {` · ${h.salidas} de ${h.total} salidas`}
                </em>,
              ),
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default ChoferesDelTractor;
